/**
 * RecommendationsList - Displays actionable recommendations
 */

import React, {useState} from "react";
import {Card, CardBody, Badge, Collapse, Button, Row, Col} from "reactstrap";
import {
  Recommendation,
  Issue,
  IssuePriority,
  RecommendationDifficulty,
  TimeInvestment
} from "../../types/advancedAnalysis";
import DOMPurify from "dompurify";

interface RecommendationsListProps {
  recommendations: Recommendation[];
  issues: Issue[];
}

const getPriorityColor = (priority: IssuePriority): string => {
  switch (priority) {
    case "critical":
      return "danger";
    case "high":
      return "warning";
    case "medium":
      return "info";
    case "low":
      return "secondary";
    default:
      return "secondary";
  }
};

const getDifficultyColor = (difficulty: RecommendationDifficulty): string => {
  switch (difficulty) {
    case "easy":
      return "success";
    case "moderate":
      return "warning";
    case "advanced":
      return "danger";
    default:
      return "secondary";
  }
};

const getTimeInvestmentColor = (timeInvestment: TimeInvestment): string => {
  switch (timeInvestment) {
    case "instant":
      return "success";
    case "quick":
      return "success";
    case "moderate":
      return "info";
    case "significant":
      return "warning";
    case "extended":
      return "warning";
    case "major":
      return "danger";
    default:
      return "secondary";
  }
};

const getTimeInvestmentLabel = (timeInvestment: TimeInvestment): string => {
  switch (timeInvestment) {
    case "instant":
      return "< 1 min";
    case "quick":
      return "1-5 min";
    case "moderate":
      return "5-15 min";
    case "significant":
      return "15-30 min";
    case "extended":
      return "30-60 min";
    case "major":
      return "1+ hours";
    default:
      return timeInvestment;
  }
};

const RecommendationCard: React.FC<{recommendation: Recommendation; relatedIssue?: Issue}> = ({
  recommendation,
  relatedIssue
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggle = () => setIsOpen(!isOpen);

  // Sanitize HTML content
  const sanitizedDescription = DOMPurify.sanitize(recommendation.Description);
  const sanitizedExpectedImpact = DOMPurify.sanitize(recommendation.ExpectedImpact);

  // Format title with activity names
  const activitySuffix =
    recommendation.ActivityNames && recommendation.ActivityNames.length > 0
      ? ` in ${recommendation.ActivityNames.join(", ")} ${recommendation.ActivityNames.length === 1 ? "activity" : "activities"}`
      : "";
  const displayTitle = recommendation.Title + activitySuffix;

  return (
    <Card className="mb-3 recommendation-card">
      <CardBody>
        <div className="card-header-row">
          <h5 className="mb-2 card-title-text">
            <i className="fa fa-lightbulb mr-2"></i>
            {displayTitle}
          </h5>
          <Button color="link" onClick={toggle} size="sm" className="card-toggle-btn">
            {isOpen ? (
              <>
                <i className="fa fa-chevron-up mr-1"></i>
                Hide
              </>
            ) : (
              <>
                <i className="fa fa-chevron-down mr-1"></i>
                Show
              </>
            )}
          </Button>
        </div>
        {relatedIssue && (
          <p className="text-muted small mb-2">
            <i className="fa fa-link mr-1"></i>
            Related to: {relatedIssue.Title}
          </p>
        )}
        <div className="mb-2">
          <small className="text-muted d-block">
            <strong>Priority:</strong> {recommendation.Priority} • <strong>Difficulty:</strong>{" "}
            {recommendation.DifficultyLevel} • <strong>Time:</strong>{" "}
            {getTimeInvestmentLabel(recommendation.TimeInvestment)}
          </small>
        </div>

        <Collapse isOpen={isOpen}>
          <hr />
          <div className="mt-3">
            <h6 className="font-weight-bold">Steps</h6>
            {recommendation.Actions && recommendation.Actions.length > 0 ? (
              <ol className="recommendation-steps analysis-html-content">
                {recommendation.Actions.map((action, index) => {
                  const sanitizedActionDescription = DOMPurify.sanitize(action.Description);
                  return (
                    <li key={index} className="mb-2">
                      <div dangerouslySetInnerHTML={{__html: sanitizedActionDescription}} />
                      {action.IsOptional && (
                        <Badge color="info" className="ml-2">
                          Optional
                        </Badge>
                      )}
                    </li>
                  );
                })}
              </ol>
            ) : (
              <div className="analysis-html-content" dangerouslySetInnerHTML={{__html: sanitizedDescription}} />
            )}
          </div>

          <div className="mt-3">
            <h6 className="font-weight-bold">Expected Result</h6>
            <div className="analysis-html-content" dangerouslySetInnerHTML={{__html: sanitizedExpectedImpact}} />
          </div>
        </Collapse>
      </CardBody>
    </Card>
  );
};

const RecommendationsList: React.FC<RecommendationsListProps> = ({recommendations, issues}) => {
  if (recommendations.length === 0) {
    return (
      <div className="alert alert-success">
        <i className="fa fa-check-circle mr-2"></i>
        No recommendations at this time. Your configuration appears optimal for the observed performance.
      </div>
    );
  }

  // Create a map of issue IDs to issues for quick lookup
  const issueMap = issues.reduce(
    (acc, issue) => {
      acc[issue.Id] = issue;
      return acc;
    },
    {} as Record<string, Issue>
  );

  // Sort recommendations by priority
  const sortedRecommendations = [...recommendations].sort((a, b) => {
    const priorityOrder = {critical: 0, high: 1, medium: 2, low: 3};
    return priorityOrder[a.Priority] - priorityOrder[b.Priority];
  });

  return (
    <div>
      {sortedRecommendations.map((recommendation) => (
        <RecommendationCard
          key={recommendation.Id}
          recommendation={recommendation}
          relatedIssue={recommendation.IssueId ? issueMap[recommendation.IssueId] : undefined}
        />
      ))}
    </div>
  );
};

export default RecommendationsList;
